/** @format */
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import {
    ListingPageType,
    ListingPageVariant,
} from '@toucan/client/features/search/components/listing-page';
import {
    SortByOption,
    SortDirection,
} from '@toucan/client/features/search/config/types';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface UseSortByOptionsReturn {
    categorySortByOptions: SortByOption[];
    defaultSortByOption?: SortByOption;
}

/**
 * Gets the sort by options for the listing page
 *
 * @param t Translation function
 * @param variant Variant of the listing page that is being displayed
 * @param outOfStock If out of stock products are being shown
 */
export const useSortByOptions = (
    variant: ListingPageVariant,
    outOfStock: boolean,
    type?: ListingPageType
): UseSortByOptionsReturn => {
    const { t } = useTranslation();
    const router = useRouter();

    const isModelPage = router.pathname === '/model';
    const isCategoryPage = type === ListingPageType.CATEGORY;
    const isBrandPage = type === ListingPageType.BRAND;
    const isProductListingPage = variant === ListingPageVariant.PRODUCT;
    const isBrandOrCategoryPage = isBrandPage || isCategoryPage;

    const categorySortByOptions: SortByOption[] = [];

    const findOptionByFacetValue = (facet: string, value: string) => {
        return categorySortByOptions.find((option) => {
            return option.facet === facet && option.value === value;
        });
    };

    // Checking specifically against template path so can keep these options on FacetedListingPage
    if (!isModelPage) {
        if (!isBrandOrCategoryPage) {
            categorySortByOptions.push({
                title: t('Relevance', { context: 'sortOptions' }),
                value: 'score',
                facet: 'relevance',
                direction: SortDirection.ASC,
            });
        }

        categorySortByOptions.push(
            ...[
                {
                    title: t('A-Z', { context: 'sortOptions' }),
                    value: 'a_z',
                    facet: 'model_name',
                    direction: SortDirection.ASC,
                },
                {
                    title: t('Z-A', { context: 'sortOptions' }),
                    value: 'z_a',
                    facet: 'model_name',
                    direction: SortDirection.DESC,
                },
            ]
        );
    }

    if (!outOfStock) {
        categorySortByOptions.push(
            ...[
                {
                    title: t('Price high/low', { context: 'sortOptions' }),
                    value: 'price_high_low',
                    facet: 'product_price',
                    direction: SortDirection.DESC,
                },
                {
                    title: t('Price low/high', { context: 'sortOptions' }),
                    value: 'price_low_high',
                    facet: 'product_price',
                    direction: SortDirection.ASC,
                },
            ]
        );
    }

    if (isProductListingPage) {
        categorySortByOptions.push({
            title: t('Newly listed', { context: 'sortOptions' }),
            value: 'product_last_online',
            facet: 'product_last_online',
            direction: SortDirection.DESC,
        });
    }

    const getDefaultSortByOption = () => {
        if (outOfStock && isBrandOrCategoryPage) {
            return findOptionByFacetValue('model_name', 'a_z');
        }

        if (isBrandOrCategoryPage || isProductListingPage) {
            return findOptionByFacetValue('product_price', 'price_high_low');
        }

        return findOptionByFacetValue('relevance', 'score');
    };

    const defaultSortByOption = getDefaultSortByOption();

    return {
        defaultSortByOption,
        categorySortByOptions,
    };
};
